import { insightsIntl } from "../strings/intl";
import { DateRangeOptions } from "@wayflyer/flyui";
import { type IntlShape, useIntl } from "react-intl";
import type { ReferencedChartTabProps } from "@wayflyer/flyui-dataviz";
import { MetricReferenceDirection } from "@wayflyer/flyui-dataviz";
import React from "react";
import { roundToNumDecimals } from "shared-utils";
import { isNumber } from "lodash";
import { addError } from "@wayflyer/blackbox-fe";

import type { DataPoint } from "../utils/fetch-api";
import { getDashboardOutput } from "../utils/fetch-api";
import { Dimensions, getMetricOverrideFromMetricType, MetricTypes, PINNABLE_METRICS } from "../autogen/metrics";
import { metricDescription, metricTooltip } from "../strings/metric-messages";
import type { CustomTimeRange, Metric } from "../utils/types";
import type { DashboardConfigurationData } from "../utils/dashboard-configuration-api";
import {
  getDashboardConfigurationData,
  initialDashboardConfiguration,
  storeDashboardConfigurationData,
  useDashboardConfiguration,
} from "../utils/dashboard-configuration-api";
import { formatValue } from "../strings/type-formatting";
import type { DashboardMetadata } from "../utils/fetch-dashboard-metadata";
import { getNewCustomerPercentMetric, getReturnRateMetric } from "../products/metrics";
import { yoyReferenceForTimeRange } from "../utils/custom-time-range-helpers";
import type { OverviewProps } from "../interface";

type SummaryOutput = { summary: DataPoint; currency?: string };

const getSummaryData = (
  dashboardDataPath: string,
  selectedTimeRange: CustomTimeRange,
  metadata: DashboardMetadata,
  cb: (d: DataPoint, currency: string) => void
) => {
  const simpleMetrics = new Promise<SummaryOutput>((resolve, reject) => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: [MetricTypes.GOOGLE_AD_SPEND, MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS],
          filename: "google_insights_ctr",
        },
        {
          metrics: [MetricTypes.META_AD_SPEND, MetricTypes.META_AD_ATTRIBUTED_CONVERSION_ROAS],
          filename: "meta_insights_overview_ctr",
        },
        {
          metrics: [
            MetricTypes.TOTAL_ORDER_QUANTITY,
            MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY,
            MetricTypes.AVERAGE_UNITS_PER_ORDER,
            MetricTypes.TOTAL_REFUNDED_ITEM_QUANTITY,
            MetricTypes.SESSION_COUNT,
            MetricTypes.CONVERSION_RATE,
          ],
          filename: "overview_insights",
        },
        // {
        //   metrics: [MetricTypes.THREE_MONTH_LTV, MetricTypes.SIX_MONTH_LTV, MetricTypes.TWELVE_MONTH_LTV],
        //   filename: "ltv_cac_daily_insights",
        // },
      ],
      selectedTimeRange,
      (data) => {
        resolve({ summary: data.summary, currency: data.currencyCode });
      },
      undefined,
      (e) => reject(e)
    );
  });
  const newCustomerPercentMetric = getNewCustomerPercentMetric(metadata);
  const returnRateMetric = getReturnRateMetric(metadata);

  const productsMetrics = new Promise<SummaryOutput>((resolve, reject) => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: [newCustomerPercentMetric, returnRateMetric].filter(Boolean) as MetricTypes[],
          filename: "products_insights",
          groupBys: [Dimensions.PRODUCT],
        },
      ],
      selectedTimeRange,
      (data) => {
        resolve({ summary: data.summary, currency: data.currencyCode });
      },
      Dimensions.PRODUCT,
      (e) => reject(e)
    );
  });

  const tiktokMetrics = new Promise<SummaryOutput>((resolve, reject) => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: [MetricTypes.TIKTOK_AD_SPEND],
          filename: "tiktok_insights_ctr",
          groupBys: [Dimensions.AD_ACCOUNT_ID],
        },
      ],
      selectedTimeRange,
      (data) => {
        resolve({ summary: data.summary, currency: data.currencyCode });
      },
      Dimensions.AD_ACCOUNT_ID,
      (e) => reject(e)
    );
  });

  Promise.all([simpleMetrics, productsMetrics, tiktokMetrics]).then((values) => {
    const combinedValues = values.map((value) => value.summary).reduce((r, c) => Object.assign(r, c), {});
    combinedValues[MetricTypes.RETURNING_CUSTOMER_PERCENTAGE] = 1 - +(combinedValues[newCustomerPercentMetric] ?? 0);
    if (values.length && values[0].currency) {
      cb(combinedValues, values[0].currency);
    }
  });
};

// Metrics that we want to pull out of existing overview data
const PINNED_METRICS_TO_EXTRACT: MetricTypes[] = [
  MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX,
  MetricTypes.AOV,
  MetricTypes.ROAS,
];

type PinnedMetricData = {
  metricValue: string;
  metricReferenceValue?: string;
  metricReferenceDirection?: MetricReferenceDirection;
  metricReferenceSuccess?: boolean;
};

const getMetricDataFromSummary = (
  summaryData: ReferencedChartTabProps[],
  metricType: MetricTypes
): PinnedMetricData | null => {
  const tab = summaryData.find((tab) => tab.metricType === metricType);
  if (!tab) {
    return null;
  }
  return {
    metricValue: tab.metricValue ?? "",
    metricReferenceValue: tab.metricReferenceValue ?? "",
    metricReferenceDirection: tab.metricReferenceDirection as MetricReferenceDirection,
    metricReferenceSuccess: tab.metricReferenceSuccess,
  };
};

const getMetricDataFromData = (
  currentData: DataPoint,
  refData: DataPoint,
  metricType: MetricTypes,
  currency: string
): PinnedMetricData => {
  const referenceValue = +(refData[metricType] ?? 0);
  const metric = getMetricOverrideFromMetricType(metricType, currency);
  const value = currentData[metricType] ?? 0;
  const valString = metric?.type === "PERCENTAGE" ? (+value * 100)?.toString() : value?.toString();
  const metricValue = formatValue({
    value: valString,
    metricType: metric as Metric,
  });
  if (!referenceValue || referenceValue === 0 || isNaN(referenceValue) || !isNumber(value)) {
    return { metricValue };
  }
  const refPercentage = roundToNumDecimals(100 * (+value - referenceValue)) / referenceValue;
  const metricReferenceDirection = getSuccessDirection(refPercentage);

  const metricReferenceSuccess = refPercentage > 0 === (metric?.success_direction === "up");

  const metricReferenceValue = formatValue({
    value: refPercentage.toString(),
    metricType: { type: "PERCENTAGE" },
  });
  return {
    metricValue,
    metricReferenceValue,
    metricReferenceDirection,
    metricReferenceSuccess,
  };
};

const getSuccessDirection = (percentageValue: number): MetricReferenceDirection => {
  if (percentageValue === 0) {
    return MetricReferenceDirection.SAME;
  }
  if (percentageValue > 0) {
    return MetricReferenceDirection.UP;
  }
  return MetricReferenceDirection.DOWN;
};

const getMetricsToShow = (metaData: DashboardMetadata): MetricTypes[] => {
  const metrics = [...PINNABLE_METRICS];

  const removeValueInPlace = (array: MetricTypes[], m: MetricTypes) => {
    array.splice(array.indexOf(m), 1);
  };

  const removeValue = (array: MetricTypes[], m: MetricTypes) => {
    const newArray = [...array];
    if (newArray.indexOf(m) < 0) {
      return newArray;
    }

    newArray.splice(newArray.indexOf(m), 1);
    return newArray;
  };

  // Keep only the metric that we want according to metadata
  const customerPercentMetric = getNewCustomerPercentMetric(metaData);
  removeValue(
    [MetricTypes.ITEM_LEVEL_NEW_CUSTOMER_PERCENT_FROM_PRODUCTS, MetricTypes.ITEM_LEVEL_NEW_CUSTOMER_PERCENT],
    customerPercentMetric
  ).forEach((m) => removeValueInPlace(metrics, m));

  // Keep only the metric that we want according to metadata
  const returnRateMetric = getReturnRateMetric(metaData);
  if (returnRateMetric) {
    removeValue(
      [MetricTypes.ITEM_LEVEL_RETURN_RATE_FROM_PRODUCTS, MetricTypes.ITEM_LEVEL_RETURN_RATE_FROM_REFUNDS],
      returnRateMetric
    ).forEach((m) => removeValueInPlace(metrics, m));
  }

  if (!metaData.google_ads_connected) {
    removeValueInPlace(metrics, MetricTypes.GOOGLE_AD_SPEND);
    removeValueInPlace(metrics, MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS);
  }

  if (!metaData.meta_ads_connected) {
    removeValueInPlace(metrics, MetricTypes.META_AD_SPEND);
    removeValueInPlace(metrics, MetricTypes.META_AD_ATTRIBUTED_CONVERSION_ROAS);
  }

  if (!metaData.tiktok_ads_connected) {
    removeValueInPlace(metrics, MetricTypes.TIKTOK_AD_SPEND);
  }

  return metrics;
};

const generatePinnedMetrics = (
  currentData: DataPoint,
  refData: DataPoint,
  pinnableMetrics,
  summaryData: ReferencedChartTabProps[],
  onEnable: (m: MetricTypes) => (e: boolean) => void,
  metaData: DashboardMetadata,
  currency: string,
  intl: IntlShape
): OverviewProps["pinnedMetricsProps"]["metrics"] => {
  return getMetricsToShow(metaData)
    .map((metricType) => {
      let metricData;
      // We do some magic to munge stuff into the overview data, just use that for appropriate metrics
      if (PINNED_METRICS_TO_EXTRACT.includes(metricType)) {
        metricData = getMetricDataFromSummary(summaryData, metricType);
      } else {
        metricData = getMetricDataFromData(currentData, refData, metricType, currency);
      }

      if (!metricData) {
        return null;
      }
      return {
        ...metricData,
        title: metricDescription(intl, metricType),
        tooltip: metricTooltip(intl, metricType),
        metricType: metricType,
        enabled: pinnableMetrics[metricType],
        onEnable: onEnable(metricType),
        metricReferenceText: insightsIntl.formatMessage({ id: "vs last year", defaultMessage: "vs last year" }),
      };
    })
    .filter((x) => x);
};

export const usePinnedMetrics = (
  selectedTimeRange: CustomTimeRange,
  summaryData: ReferencedChartTabProps[],
  dashboardConfigurationPath: string,
  dashboardDataPath: string,
  metadata: DashboardMetadata,
  currentPreset: DateRangeOptions
): OverviewProps["pinnedMetricsProps"]["metrics"] => {
  const intl = useIntl();
  const [metrics, setMetrics] = React.useState<OverviewProps["pinnedMetricsProps"]["metrics"]>([]);
  const [pinnedMetricsData, setPinnedMetricsData] = React.useState<DataPoint | null>(null);
  const [pinnedMetricsDataRef, setPinnedMetricsDataRef] = React.useState<DataPoint | null>(null);
  const [currency, setCurrency] = React.useState<string | null>(null);

  const [localDashboardConfig, setLocalDashboardConfig] = useDashboardConfiguration(dashboardConfigurationPath);
  const [needsMetricComputation, setNeedsMetricComputation] = React.useState<boolean>(false);

  React.useEffect(() => {
    getDashboardConfigurationData(
      dashboardConfigurationPath,
      (dashboardConfigurationData: DashboardConfigurationData) => {
        setLocalDashboardConfig(dashboardConfigurationData);
      },
      (failureMessage: string) => {
        addError(failureMessage);
        setLocalDashboardConfig({ ...initialDashboardConfiguration });
      }
    );
  }, [dashboardConfigurationPath, setLocalDashboardConfig]);

  React.useEffect(() => {
    if (metadata && selectedTimeRange && summaryData.length && summaryData[0].metricValue != null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setNeedsMetricComputation(true);
      setPinnedMetricsData(null);
      setPinnedMetricsDataRef(null);

      getSummaryData(dashboardDataPath, selectedTimeRange, metadata, (d, c) => {
        setPinnedMetricsData(d);
        setCurrency(c);
      });

      getSummaryData(dashboardDataPath, yoyReferenceForTimeRange(selectedTimeRange, currentPreset), metadata, (d) => {
        setPinnedMetricsDataRef(d);
      });
    }
  }, [currentPreset, dashboardDataPath, metadata, selectedTimeRange, summaryData]);

  React.useEffect(() => {
    if (
      needsMetricComputation &&
      pinnedMetricsData &&
      pinnedMetricsDataRef &&
      Object.keys(pinnedMetricsData).length &&
      summaryData.length &&
      summaryData[0].metricValue != null &&
      metadata &&
      currency
    ) {
      const onEnable = (metricType: MetricTypes) => (enabled: boolean) => {
        const newConfig = { ...localDashboardConfig };
        newConfig.pinnable_metrics[metricType] = enabled;
        storeDashboardConfigurationData(
          dashboardConfigurationPath,
          localDashboardConfig,
          () => {
            setLocalDashboardConfig(newConfig);
            setNeedsMetricComputation(true);
          },
          (e) => {
            addError(e);
          }
        );
      };
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setMetrics(
        generatePinnedMetrics(
          pinnedMetricsData,
          pinnedMetricsDataRef,
          localDashboardConfig.pinnable_metrics,
          summaryData,
          onEnable,
          metadata,
          currency,
          intl
        )
      );
      setNeedsMetricComputation(false);
    }
  }, [
    localDashboardConfig,
    dashboardConfigurationPath,
    pinnedMetricsData,
    pinnedMetricsDataRef,
    setLocalDashboardConfig,
    summaryData,
    metadata,
    needsMetricComputation,
    currency,
    intl,
  ]);

  return metrics;
};
